// Prepares an agent-authored HTML report for display inside the doc viewer's sandboxed `srcDoc` iframe.
//
// WE DO NOT SANITIZE, AND DELIBERATELY DO NOT STRIP `<script>`. The frame is served from an opaque
// origin with `sandbox="allow-scripts …"` and no `allow-same-origin`, so script in the document cannot
// reach our storage, cookies, session or DOM — the sandbox is the containment. A regex "sanitizer" on
// top of that would be strictly worse than nothing: HTML has a dozen ways to smuggle a script past one
// (`<svg/onload>`, `<iframe srcdoc>`, entity-encoded attributes, mutation-XSS on re-parse), so it buys
// no security while creating false confidence that the content is safe — and reports legitimately embed
// chart scripts, which it would break. If script ever needs blocking, that is a CSP + request-blocking
// decision taken across all three surfaces at once, not a regex here.

/** The `<base href>` for a document fetched from `url`: its containing directory, so relative
 *  sub-resources (`img/chart.png`, `./style.css`) resolve against the document's own location rather
 *  than ours. Fragment and query are dropped. Null when `url` isn't absolute. */
export function documentBaseHref(url: string): string | null {
  try {
    const u = new URL(url);
    u.hash = '';
    u.search = '';
    const path = u.pathname;
    u.pathname = path.slice(0, path.lastIndexOf('/') + 1);
    return u.toString();
  } catch {
    return null;
  }
}

/** A leading doctype, kept as the very first thing in the document.
 *
 *  Not for the reason you'd expect, and the reason this comment used to give was WRONG on both counts:
 *  emitting head elements ahead of `<html>` loses nothing (the parser hoists them into the head, and a
 *  later `<html lang="…">` merges its attributes onto the element that already exists), and it cannot
 *  cause quirks mode in a srcdoc document — the parser's quirks arm is gated on the "cannot change the
 *  mode" flag, which srcdoc documents set, so their mode is fixed as no-quirks regardless of what precedes
 *  the doctype.
 *
 *  We keep the code anyway because the OUTPUT is what a reviewer reads and what gets pasted elsewhere: a
 *  document that still opens with its doctype is recognisably the author's document, and it stays correct
 *  if this string is ever rendered somewhere that is NOT a srcdoc frame (a `data:` URL, a downloaded file,
 *  a future server-side render), where the mode-locking no longer applies. It costs one regex. */
const LEADING_DOCTYPE = /^\s*<!doctype[^>]*>/i;

/** The CSP we stamp into the framed document.
 *
 *  This is a MITIGATION, not the natives' `decide()` gate. The natives intercept every navigation the
 *  document attempts and cancel all of them, opening only user-activated links externally. **An iframe
 *  cannot intercept its own navigation** — there is no web equivalent of `WKNavigationDelegate` for
 *  content we don't control — so the truth table the natives enforce is, on web, approximated by these two
 *  layers: this CSP (which stops the navigations a directive CAN stop) and the frame-load guard below
 *  (which detects the ones it can't and tears the frame down).
 *
 *  Directive by directive, and why each is what it is — the constraint is that a real agent-authored report
 *  must keep working (inline `<style>`/`<script>`, a remote chart library, remote CSS, remote images):
 *
 *  - `default-src * data: blob:` — the permissive floor every other directive narrows. Sub-resources
 *    loading is a DELIBERATE decision taken across all three surfaces (reports embed remote chart assets);
 *    blocking them is a separate, three-surface decision, not something to half-do here.
 *  - `script-src * data: blob: 'unsafe-inline' 'unsafe-eval'` — scripting is on by design; the *sandbox*
 *    (opaque origin, no `allow-same-origin`) is what contains it. `'unsafe-eval'` is here because chart
 *    libraries template through `Function`, and withholding it would break reports without denying an
 *    attacker anything they don't already have via inline script.
 *  - `style-src * data: 'unsafe-inline'` — reports style themselves inline and from CDNs.
 *  - `img-src * data: blob:` — charts and logos.
 *  - **`form-action 'none'`** — the one directive that BUYS something. A `<form action>` submit is a
 *    navigation of the frame itself, which no sandbox token prevents (`allow-top-navigation` is about the
 *    TOP frame) and which the guard below can only notice after the fact. This blocks it outright.
 *  - `object-src 'none'` — no plugin embeds; nothing legitimate needs them and they are a navigation and
 *    parsing surface of their own.
 *
 *  Deliberately NOT set: `base-uri` (we inject our own `<base href>` and `'none'` would void it, while
 *  first-base-wins already makes ours authoritative), and `frame-src`/`child-src` (a report embedding a
 *  video iframe is legitimate, and a nested frame inherits our sandbox).
 *
 *  CSP CANNOT constrain the referrer. The `referrer` directive was dropped from the spec, so an author
 *  `<meta name="referrer" content="unsafe-url">` cannot be countered here — see `headBlock`. */
const FRAME_CSP = [
  "default-src * data: blob:",
  "script-src * data: blob: 'unsafe-inline' 'unsafe-eval'",
  "style-src * data: 'unsafe-inline'",
  "img-src * data: blob:",
  "form-action 'none'",
  "object-src 'none'",
].join('; ');

/** Does this look like a whole document rather than a bare fragment? Only decides whether we WRAP: a
 *  fragment is wrapped (and gets a doctype, so it renders in standards mode), a document is prefixed.
 *  Being wrong is cosmetic either way — the parser hoists our head elements and synthesises
 *  `<html>`/`<head>`/`<body>` for a fragment regardless. */
const DOCUMENT_SHAPED = /<(?:html|head|body)[\s>]/i;

// WRITING DIRECTION IS THE AUTHOR'S, ON BOTH BRANCHES — deliberately, unlike the markdown view.
//
// `MarkdownDocView` decides `dir` once from the majority script, because markdown carries no direction of
// its own and the alternative (`dir="auto"`) guesses per block. An HTML report is the opposite case:
//
//  1. **It has its own vocabulary for this** — `<html dir>`, `lang`, and CSS `direction` — so the author
//     already has a real channel, and overriding a document that states its direction would be worse than
//     the guess we are replacing.
//  2. **We could not honour "only when the document doesn't set its own" anyway.** Deciding that means
//     locating the author's `<html>`/`dir`/`direction` in untrusted markup with a regex, which is the exact
//     trap documented at the top of this file: it already shipped once, matching a `<head>` inside a
//     comment and injecting our head block where it was inert.
//  3. **The majority-script helper cannot read HTML.** `messageIsRtl` strips markdown, not tags — over raw
//     markup the Latin in every tag name and attribute swamps the prose, so it would answer LTR for
//     essentially every document, including a Hebrew one.
//
// Setting it on the synthesised `<html>` of the FRAGMENT branch alone was considered and rejected: the same
// report would then classify differently depending on whether the agent happened to emit a `<body>` tag.
// Residual, stated plainly: a Hebrew HTML report that sets no direction of its own renders LTR. The honest
// fix is an HTML text extractor feeding the shared verdict, on all three surfaces at once — not a regex here.
function headBlock(baseHref?: string | null): string {
  // Our <base> comes FIRST in document order: the HTML spec resolves the document base URL from the
  // first <base> with an href and the default target from the first <base> with a target, so ours wins
  // over an author-supplied one without us having to rewrite their markup.
  const base = baseHref
    ? `<base href="${escapeAttribute(baseHref)}" target="_blank">`
    : '<base target="_blank">';
  return [
    '<meta charset="utf-8">',
    // The policy that stops the document navigating ITSELF via a form submit, and the only layer that can:
    // see FRAME_CSP. First in document order so it applies to everything the author writes after it.
    `<meta http-equiv="Content-Security-Policy" content="${escapeAttribute(FRAME_CSP)}">`,
    '<meta name="viewport" content="width=device-width,initial-scale=1">',
    // A best-effort referrer lock, and honestly only that. Referrer metas are processed in DOCUMENT ORDER
    // and each one REPLACES the policy (unlike `<base>`, where first-with-an-href wins — which is what makes
    // our base injection authoritative), so an author `<meta name="referrer" content="unsafe-url">` later in
    // the document overrides ours for every subsequent sub-resource request. CSP cannot help: the `referrer`
    // directive was removed from the spec.
    //
    // RESIDUAL, stated plainly: a srcdoc frame's sub-resource requests carry the PARENT document's URL as
    // their referrer, so a report that raises its own referrer policy can leak the orion-web URL — including
    // `?file=<id>` and the conversation id — to any host it loads an image or script from. That is a
    // consequence of the deliberate "sub-resources load" decision (reports embed remote chart assets), and
    // the only real fix is host-restricting `img-src`/`script-src`, which is a three-surface decision
    // (`WKContentRuleList` + `shouldInterceptRequest` + CSP), not a web-only tweak. We do NOT try to strip
    // the author's meta: locating it needs a regex over untrusted markup, which is the same trap documented
    // at the top of this file.
    '<meta name="referrer" content="no-referrer">',
    base,
    // A paper-white light baseline BEFORE any author CSS (so authors can still override it): an
    // unstyled document is otherwise transparent over the modal scrim, and a UA in dark mode would
    // render an author's black text on its own dark canvas. Design tokens can't reach in here — this is
    // a separate document with its own CSS scope — so these are plain CSS keywords by necessity.
    '<style>:root{color-scheme:light}html,body{background:white}body{margin:16px}</style>',
  ].join('');
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** The document to hand the viewer's `srcDoc` iframe: our head block emitted BEFORE the author's markup
 *  (after a leading doctype, which must stay first), and a bare fragment wrapped into a real document.
 *
 *  We do NOT go looking for the author's `<head>` to inject into. Locating it needs a regex, and a regex
 *  can't tell markup from text: `<!-- <head> -->` or a `<head>` inside a script string matched just as
 *  well, and injecting there put our `<base>`/referrer meta INSIDE the comment where they are inert —
 *  silently, with the visible symptom being a transparent report with dead links. Emitting ahead of the
 *  markup needs no parsing and is what the spec already guarantees for us: stray head elements are
 *  hoisted into the head, and the FIRST `<base>` in document order wins, which is the only property we
 *  actually need.
 *
 *  Not idempotent by design, and it must not become so: the caller frames each document exactly once
 *  (`HtmlDocView`'s `useMemo`), and the marker that used to make it idempotent lived in the document
 *  BODY — so any report merely quoting that marker (this repo's own source does) opted itself out of the
 *  whole head block. Content-sniffed idempotency is author-controllable; it bought nothing and broke
 *  documents. */
export function prepareHtmlDocument(html: string, baseHref?: string | null): string {
  const head = headBlock(baseHref);
  // Comments are stripped for the wrap decision only (never for the output): a fragment that merely
  // QUOTES `<head>`/`<body>` in a comment would otherwise be taken for a whole document and lose the
  // doctype the wrap gives it.
  if (!DOCUMENT_SHAPED.test(html.replace(/<!--[\s\S]*?-->/g, ''))) {
    return `<!DOCTYPE html><html><head>${head}</head><body>${html}</body></html>`;
  }

  const doctype = LEADING_DOCTYPE.exec(html);
  const at = doctype ? doctype[0].length : 0;
  return `${html.slice(0, at)}${head}${html.slice(at)}`;
}

// ---------------------------------------------------------------------------------------------------
// The frame-load guard
//
// No CSP directive can stop a SCRIPTED navigation (`location.href = …`, `<a target="_self">.click()`) or a
// `<meta http-equiv="refresh">`, and an iframe cannot intercept its own navigation. So we detect it instead:
// the frame's FIRST `load` is our own document; a SECOND `load` means something replaced it, and whatever is
// in there now is a foreign page wearing our chrome and the user's file name. At that point the frame is torn
// down and an error is shown, because continuing to display it is the actual attack (a report containing
// `<meta http-equiv="refresh" content="0;url=https://evil/wix-login">` otherwise swaps itself for a
// credential form inside the Symphony modal, titled with the user's own report name).
//
// The state is keyed on the document string so the three non-events can't trip it:
//   - the INITIAL load (loads === 1) is ours,
//   - a React re-render fires no `load` at all, so the count doesn't move, and
//   - a legitimately NEW document (`srcDoc` changed) starts a fresh count rather than inheriting the old one.
// ---------------------------------------------------------------------------------------------------

/** How many documents the frame has loaded, and which document we are counting for. */
export interface FrameLoadState {
  doc: string;
  loads: number;
}

export type FrameLoadEvent = { type: 'document'; doc: string } | { type: 'load' };

export const initialFrameLoadState = (doc: string): FrameLoadState => ({ doc, loads: 0 });

/** The guard's reducer. A `document` event for the SAME document is a no-op (so a re-render that recomputes
 *  the same string cannot reset a guard that has already tripped); for a different one it starts over. */
export function frameLoadReducer(state: FrameLoadState, event: FrameLoadEvent): FrameLoadState {
  if (event.type === 'document') return event.doc === state.doc ? state : initialFrameLoadState(event.doc);
  return { doc: state.doc, loads: state.loads + 1 };
}

/** True when the frame is showing something that is NOT `doc` any more. Takes the current document as well
 *  as the state so a stale count (the render between a new document arriving and the reducer being told
 *  about it) reads as "fine" rather than flashing the error state. */
export function frameNavigatedAway(state: FrameLoadState, doc: string): boolean {
  return state.doc === doc && state.loads > 1;
}
